import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EstimateView } from '@/routes/(public)/estimate.$id';
import type { QuoteActionTransport } from '@/routes/(public)/quoteActionContext';
import {
  portalChargeDepositPayabli,
  portalConfirmDeposit,
  usePortalApproveEstimate,
  usePortalCreateDepositSession,
  usePortalRejectEstimate,
  type PortalEstimateDetail,
} from '@/hooks/portal/usePortalEstimates';
import { useDepositConfirmOnReturn } from '@/lib/quote/depositSettlement';
import { usePortalAuthStore } from '@/stores/portalAuth.store';

/** Always use the shared Single Scope document, with portal-owned actions. */
export function PortalSingleScopeEstimate({
  data,
  id,
  deposit,
  sessionId,
}: {
  data: PortalEstimateDetail;
  id: string;
  deposit?: 'success' | 'cancel';
  sessionId?: string;
}) {
  const queryClient = useQueryClient();
  const contact = usePortalAuthStore((state) => state.contact);
  const { mutateAsync: approve } = usePortalApproveEstimate(id);
  const { mutateAsync: decline } = usePortalRejectEstimate(id);
  const { mutateAsync: createDepositSession } = usePortalCreateDepositSession(id);
  const transport = useMemo<QuoteActionTransport>(
    () => ({
      queryKey: ['portal', 'estimate', id],
      approve: (_id, _tenantId, body) => approve(body),
      decline: async (_id, _tenantId, reason) => {
        await decline(reason);
      },
      createDepositSession: (_id, method, selection) =>
        createDepositSession({ method, lineItems: selection?.lineItems }),
      chargeDeposit: portalChargeDepositPayabli,
    }),
    [id, approve, decline, createDepositSession],
  );

  useDepositConfirmOnReturn({
    deposit,
    sessionId,
    depositCollected: data.estimate.depositCollected,
    confirm: (sid) => portalConfirmDeposit(id, sid),
    onConfirmed: () => void queryClient.invalidateQueries({ queryKey: transport.queryKey }),
  });

  return (
    <EstimateView
      id={id}
      estimate={{
        ...data.estimate,
        depositCollectedSummary:
          data.depositCollectedSummary === undefined
            ? data.estimate.depositCollectedSummary
            : data.depositCollectedSummary,
      }}
      company={data.company ?? null}
      customFields={data.customFields ?? []}
      activePdf={data.activePdf ?? null}
      presentation="SINGLE_SCOPE"
      resolvedDocLayout={data.resolvedDocLayout ?? null}
      presentedByName={data.presentedBy?.name ?? null}
      reviews={data.reviews ?? null}
      depositSearchState={deposit}
      depositSurcharge={data.depositSurcharge}
      payment={data.payment ?? null}
      depositAttempt={data.depositAttempt ?? null}
      serviceFee={data.serviceFee}
      wisetack={data.wisetack ?? null}
      timezone={data.timezone}
      transport={transport}
      payerContactId={data.payerContactId ?? contact?.id ?? null}
      embedded
    />
  );
}
